import { Group, rem } from "@mantine/core"
import { IconAlertTriangle } from "@tabler/icons-react"
import type { ComponentProps } from "react"

import { TextCell } from "./TextCell"

/** Same [0, 100] finite rules as `app-front/src/utils/zod/percentage.ts` (UI cannot import app-front). */
const isPercentageOutOfValidRange = (percentage: unknown): boolean => {
  if (percentage === undefined || percentage === null) {
    return false
  }
  if (typeof percentage !== "number" || !Number.isFinite(percentage)) {
    return true
  }
  return percentage < 0 || percentage > 100
}

type TextCellProps = ComponentProps<typeof TextCell>

export type PercentageCellProps = Omit<
  TextCellProps,
  "value" | "c" | "copyable" | "onCopy"
> & {
  /** When false, shows {@link PercentageCellProps.notCalculatedLabel} with default styling */
  isCalculated: boolean
  percentage: number
  /** Default "-" */
  notCalculatedLabel?: string
  /** When true and value is outside [0, 100], shows a warning icon next to the red value */
  showOutOfRangeWarning?: boolean
}

/**
 * Table cell for a percentage: uses {@link TextCell}; shows red when the value is out of [0, 100].
 */
export const PercentageCell = ({
  isCalculated,
  percentage,
  notCalculatedLabel = "-",
  showOutOfRangeWarning = false,
  ...textCellProps
}: PercentageCellProps) => {
  if (!isCalculated) {
    return <TextCell value={notCalculatedLabel} {...textCellProps} />
  }

  const value = String(percentage)
  const outOfRange = isPercentageOutOfValidRange(percentage)

  const cell = (
    <TextCell
      value={value}
      c={outOfRange ? "red" : undefined}
      {...textCellProps}
    />
  )

  if (showOutOfRangeWarning && outOfRange) {
    return (
      <Group gap={4} wrap="nowrap" align="center">
        {cell}
        <IconAlertTriangle
          aria-hidden
          size={rem(14)}
          stroke={1.5}
          color="var(--mantine-color-orange-6)"
        />
      </Group>
    )
  }

  return cell
}
